/**
 * AgentRunDetailPage — full audit view for a single agent run.
 *
 * Shows: analysis output, action timeline, telemetry, and approval controls.
 * Route: /agent-runs/:id
 * Access: agents_view required (enforced server-side); approval controls require agents_approve.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentRunsApi } from "../services/api";
import ApprovalDecisionBrief from "../components/ApprovalDecisionBrief";
import RunOperationsTrail from "../components/RunOperationsTrail";
import SkillRoutingEvidencePanel from "../components/SkillRoutingEvidencePanel";
import { runStatusLabel } from "../components/runStatusVocabulary";
import { complexityLabel } from "../components/complexityVocabulary";
import { taskTypeLabel } from "../components/taskTypeVocabulary";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { detailText } from "../utils/apiErrors";
import { useNotification } from "../context/NotificationContext";
import { REJECT_QUICK_REASONS as _REJECT_QUICK_REASONS } from "../utils/agentRunConstants";
import { reviewShortcutAction } from "../utils/reviewShortcuts";
import FailureRecoveryPanel from "../components/FailureRecoveryPanel";
import RunQaInspectorPanel from "../components/RunQaInspectorPanel";
import { EVIDENCE_SOURCE_LABEL } from "../components/evidenceSourceVocabulary";
import { actionTypeLabel } from "../components/actionTypeVocabulary";
import {
  CORRECTION_EDIT_TYPES,
  EDIT_TYPE_LABEL,
  editTypeLabel,
} from "../components/editTypeVocabulary";

// The token-expiry rule moved with the token row into the operations trail; re-exported here because
// this page's test (and any caller that imported it from this page) reads it from this module.
export { isApprovalTokenExpired } from "../components/runOperationsTrailModel";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  awaiting_technician_review: "bg-orange-100 text-orange-700 border-orange-200",
  awaiting_approval:          "bg-orange-100 text-orange-700 border-orange-200",
  completed:                  "bg-green-100 text-green-700 border-green-200",
  resolved:                   "bg-green-100 text-green-700 border-green-200",
  failed:                     "bg-red-100 text-red-700 border-red-200",
  rejected:                   "bg-red-100 text-red-700 border-red-200",
  paused:                     "bg-gray-100 text-gray-600 border-gray-200",
  analyzing:                  "bg-blue-100 text-blue-700 border-blue-200",
  diagnosing:                 "bg-blue-100 text-blue-700 border-blue-200",
  executing:                  "bg-blue-100 text-blue-700 border-blue-200",
  pending:                    "bg-gray-100 text-gray-600 border-gray-200",
};

const RISK_BADGE = {
  green: "bg-green-50 text-green-700 border-green-200",
  amber: "bg-yellow-50 text-yellow-700 border-yellow-200",
  red:   "bg-red-50 text-red-700 border-red-200",
};

const RISK_LABEL = { green: "נמוך", amber: "בינוני", red: "גבוה" };

function fmtDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function Card({ title, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
      {title && <h2 className="font-semibold text-gray-700 text-sm border-b pb-2">{title}</h2>}
      {children}
    </div>
  );
}

function KV({ label, value, mono }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-gray-500 shrink-0 w-36">{label}:</span>
      <span className={`text-gray-800 ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

// ─── AnalysisSection ──────────────────────────────────────────────────────────

function AnalysisSection({ analysis, resultSummary }) {
  if (!analysis && !resultSummary) {
    return (
      <Card title="ניתוח">
        <p className="text-sm text-gray-400">לא בוצע ניתוח עדיין</p>
      </Card>
    );
  }

  const a = analysis || {};

  const confColor =
    (a.confidence || 0) >= 75 ? "text-green-600" :
    (a.confidence || 0) >= 45 ? "text-yellow-600" : "text-red-500";

  return (
    <Card title="ניתוח" testId="run-analysis-section">
      {/* A fallback analysis is boilerplate, not a diagnosis: say so before the technician reads it as one. */}
      {a.degraded && (
        <div role="alert" data-testid="degraded-analysis-banner" className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          {a.degraded === "mock"
            ? "זהו ניתוח דמה: ספק ה-AI אינו מוגדר, ולכן התוכן הבא אינו אבחון אמיתי של הקריאה."
            : "תשובת המודל לא נקראה כראוי, ולכן זהו ניתוח חלופי בביטחון נמוך ולא אבחון אמיתי. מומלץ להריץ את הניתוח מחדש."}
        </div>
      )}
      {/* Summary + Confidence */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {a.summary && <p className="text-sm text-gray-800 leading-relaxed">{a.summary}</p>}
          {resultSummary && !a.summary && <p className="text-sm text-gray-800 leading-relaxed">{resultSummary}</p>}
        </div>
        {a.confidence != null && (
          <div className="text-center shrink-0">
            <p className={`text-2xl font-bold ${confColor}`}>{a.confidence}%</p>
            <p className="text-xs text-gray-400">ביטחון</p>
          </div>
        )}
      </div>

      {/* Likely issue */}
      {a.likely_issue && (
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
          <p className="text-xs font-medium text-blue-700 mb-0.5">בעיה סבירה:</p>
          <p className="text-sm text-blue-900">{a.likely_issue}</p>
        </div>
      )}

      {/* Risk level */}
      {a.risk_level && (
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${RISK_BADGE[a.risk_level] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
            סיכון: {RISK_LABEL[a.risk_level] || a.risk_level}
          </span>
          {a.task_type && <span className="text-xs text-gray-400">{taskTypeLabel(a.task_type)}</span>}
          {a.complexity && <span className="text-xs text-gray-400">• {complexityLabel(a.complexity)}</span>}
        </div>
      )}

      {/* Recommendations */}
      {a.recommended_next_step && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">המלצה:</p>
          <p className="text-sm text-gray-700">{a.recommended_next_step}</p>
        </div>
      )}

      {a.recommended_next_diagnostic && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">אבחון ראשון לביצוע:</p>
          <p className="text-sm text-gray-700">{a.recommended_next_diagnostic}</p>
        </div>
      )}

      {/* Proposed diagnostics */}
      {a.proposed_diagnostics?.length > 0 && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer hover:text-gray-700 select-none font-medium">
            אבחונים מוצעים ({a.proposed_diagnostics.length})
          </summary>
          <ul className="mt-1 space-y-0.5 list-disc list-inside pl-2">
            {a.proposed_diagnostics.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </details>
      )}

      {/* Missing info */}
      {a.missing_information?.length > 0 && (() => {
        const lowConf = a.confidence != null && a.confidence < 60;
        return (
          <div className={lowConf ? "bg-amber-50 border border-amber-200 rounded-lg p-3" : ""}>
            <p className={`text-xs font-medium mb-1 ${lowConf ? "text-amber-700" : "text-gray-600"}`}>
              {lowConf ? "מידע חסר — ביטחון נמוך:" : "מידע חסר:"}
            </p>
            <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
              {a.missing_information.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        );
      })()}

      {/* Alternative hypotheses */}
      {a.alternative_hypotheses?.length > 0 && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer hover:text-gray-700 select-none font-medium">
            השערות חלופיות ({a.alternative_hypotheses.length})
          </summary>
          <ul className="mt-1 space-y-0.5 list-disc list-inside pl-2">
            {a.alternative_hypotheses.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </details>
      )}

      {/* Evidence sources */}
      {a.evidence_sources?.length > 0 && (
        <div data-testid="run-evidence">
          <p className="text-xs font-medium text-gray-600 mb-1">מקורות ראיות:</p>
          <div className="flex flex-wrap gap-1">
            {a.evidence_sources.map((s, i) => (
              <span key={i} className="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">{EVIDENCE_SOURCE_LABEL[s] ?? s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Proposed action */}
      {a.proposed_action && (
        <div className="border-t pt-3">
          <p className="text-xs font-medium text-blue-700 mb-1">פעולה מוצעת:</p>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 text-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-gray-800">{a.proposed_action.description}</span>
              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">בטוח</span>
            </div>
            {a.proposed_action.target && (
              <p className="text-xs text-gray-500">יעד: {a.proposed_action.target}</p>
            )}
            <p className="text-xs text-gray-400 mt-0.5">סוג: {actionTypeLabel(a.proposed_action.action_type)}</p>
          </div>
        </div>
      )}

      {/* Customer reply draft */}
      {a.customer_reply_draft && (
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-gray-600">טיוטת תגובה ללקוח:</p>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(a.customer_reply_draft)}
              className="text-[10px] text-gray-400 hover:text-gray-600 border border-gray-200 rounded px-1.5 py-0.5 hover:bg-gray-50 transition-colors"
              title="העתק ללוח"
            >
              העתק
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg border p-3 text-sm text-gray-700 whitespace-pre-wrap">
            {a.customer_reply_draft}
          </div>
        </div>
      )}

      {/* Fact/Inference/Hypothesis */}
      {a.fact_inference_hypothesis && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer hover:text-gray-700 select-none font-medium">
            עובדות / הסקות / השערות
          </summary>
          <div className="mt-2 space-y-2">
            {a.fact_inference_hypothesis.facts?.length > 0 && (
              <div>
                <p className="font-medium text-gray-600">עובדות:</p>
                <ul className="list-disc list-inside pl-2 space-y-0.5">
                  {a.fact_inference_hypothesis.facts.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
            {a.fact_inference_hypothesis.inferences?.length > 0 && (
              <div>
                <p className="font-medium text-gray-600">הסקות:</p>
                <ul className="list-disc list-inside pl-2 space-y-0.5">
                  {a.fact_inference_hypothesis.inferences.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
            {a.fact_inference_hypothesis.hypotheses?.length > 0 && (
              <div>
                <p className="font-medium text-gray-600">השערות:</p>
                <ul className="list-disc list-inside pl-2 space-y-0.5">
                  {a.fact_inference_hypothesis.hypotheses.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
          </div>
        </details>
      )}
    </Card>
  );
}

// ─── ActivityFeed ─────────────────────────────────────────────────────────────

const _ACTIVITY_STYLE = {
  customer:   { text: "text-blue-700",   bg: "bg-blue-50",    border: "border-blue-200",   label: "לקוח" },
  technician: { text: "text-teal-700",   bg: "bg-teal-50",    border: "border-teal-200",   label: "טכנאי" },
  note:       { text: "text-amber-700",  bg: "bg-amber-50",   border: "border-amber-200",  label: "הערה פנימית" },
  ai_agent:   { text: "text-purple-700", bg: "bg-purple-50",  border: "border-purple-200", label: "AI" },
};
const _ACTIVITY_RE = /^\[(customer|technician|note|ai_agent)\](?:\s*\(([^)]*)\))?\s*([\s\S]*)$/;

function ActivityFeed({ raw }) {
  const entries = (raw || "").split(/\n\n+/).filter(Boolean);
  if (!entries.length) return null;
  return (
    <div className="space-y-2">
      {entries.map((entry, i) => {
        const m = entry.match(_ACTIVITY_RE);
        if (!m) return <pre key={i} className="text-xs text-gray-600 whitespace-pre-wrap">{entry}</pre>;
        const [, role, age, text] = m;
        const s = _ACTIVITY_STYLE[role] || { text: "text-gray-700", bg: "bg-gray-50", border: "border-gray-200", label: role };
        return (
          <div key={i} className={`rounded-lg border ${s.border} ${s.bg} px-3 py-2`}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-semibold uppercase tracking-wide ${s.text}`}>{s.label}</span>
              {age && <span className="text-[10px] text-gray-400">{age}</span>}
            </div>
            <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{text.trim()}</p>
          </div>
        );
      })}
    </div>
  );
}

// ─── ApprovalControls ─────────────────────────────────────────────────────────

const REVIEWABLE = new Set(["awaiting_technician_review", "awaiting_approval"]);

// States where the operator needs the recovery brief: failures, pauses, and the waits that
// require a human. Active runs and decisions already made do not need it.
const _RECOVERY_BRIEF_STATUSES = new Set([
  ...REVIEWABLE,
  "failed",
  "paused",
  "waiting_for_customer_connection",
  "waiting_for_customer_answer",
]);
// Mirrors the backend's _STOPPABLE_STATES (routers/agent_runs.py): a paused or executing run answers 409, and a run
// waiting up to 24h for the customer's connection or answer MUST be stoppable (it is the only way out of the wait).
const STOPPABLE  = new Set(["pending", "analyzing", "diagnosing", "connection_active", "waiting_for_customer_connection", "waiting_for_customer_answer", "awaiting_technician_review", "awaiting_approval"]);

function apiErrorMessage(error) {
  const fallback = "שגיאה בשמירת התיקון ואישור הריצה";
  const detail = error?.response?.data?.detail;
  // detail may be an object ({code}) and an object as a JSX child unmounts the whole app.
  if (detail && typeof detail === "object") return detailText(detail, fallback);
  return detail
    || error?.response?.data?.message
    || error?.response?.statusText
    || error?.message
    || fallback;
}

function FeedbackHistoryRow({ feedback }) {
  const originalAction = feedback.original_ai_output?.proposed_action;
  const editedAction = feedback.technician_edited_output?.proposed_action;
  const originalReply = feedback.original_ai_output?.customer_reply_draft;
  const editedReply = feedback.technician_edited_output?.customer_reply_draft;
  return (
    <div
      data-testid={`run-feedback-${feedback.id}`}
      className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 text-xs space-y-1"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-gray-700">{editTypeLabel(feedback.edit_type)}</span>
        <span className="text-gray-500">{feedback.technician_id}</span>
      </div>
      {editedAction?.description && (
        <div className="space-y-1">
          {originalAction?.description && (
            <p className="text-gray-500 whitespace-pre-wrap"><span className="font-medium">מקור:</span> {originalAction.description}</p>
          )}
          <p className="text-gray-700 whitespace-pre-wrap"><span className="font-medium">תיקון:</span> {editedAction.description}</p>
        </div>
      )}
      {editedReply && (
        <div className="space-y-1">
          {originalReply && (
            <p className="text-gray-500 whitespace-pre-wrap"><span className="font-medium">טיוטה מקורית:</span> {originalReply}</p>
          )}
          <p className="text-gray-700 whitespace-pre-wrap"><span className="font-medium">טיוטה מתוקנת:</span> {editedReply}</p>
        </div>
      )}
      {feedback.reason && <p className="text-gray-500">סיבה: {feedback.reason}</p>}
      {feedback.approved_at && (
        <p className="text-gray-400">אושר: {new Date(feedback.approved_at).toLocaleString("he-IL")}</p>
      )}
    </div>
  );
}

function ApprovalControls({ run, onAction, historicalReasons = [], onApproveSuccess, busy = false, onFormOpenChange }) {
  const { addNotification } = useNotification();
  const { can: canAgent } = useAgentPermissions();
  const qc = useQueryClient();
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject]     = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // A form open here holds unsaved technician input (an edited customer reply, a typed reject
  // reason): the page's global "A"/"R" review shortcuts must not silently blow it away.
  useEffect(() => {
    onFormOpenChange?.(showFeedback || showReject);
    return () => onFormOpenChange?.(false);
  }, [showFeedback, showReject, onFormOpenChange]);
  const [feedbackReason, setFeedbackReason] = useState("");
  const [rememberForCustomer, setRememberForCustomer] = useState(false);
  const [rememberResolution, setRememberResolution] = useState(false);
  const proposedType = run.analysis?.proposed_action?.action_type;
  const _defaultEditType = proposedType === "send_customer_message" ? "reply_edit" : proposedType === "close_ticket" ? "closing_edit" : "action_edit";
  const [feedbackEditType, setFeedbackEditType] = useState(_defaultEditType);
  const [editedReply, setEditedReply] = useState(
    run.analysis?.customer_reply_draft || ""
  );
  const [editedComment, setEditedComment] = useState(
    run.analysis?.proposed_action?.description || ""
  );

  const _EDITABLE_DESC_TYPES = new Set(["add_comment", "close_ticket", "escalate"]);

  const _buildEditedOutput = () => {
    if (proposedType === "send_customer_message") {
      return { customer_reply_draft: editedReply };
    }
    if (_EDITABLE_DESC_TYPES.has(proposedType)) {
      const base = { proposed_action: { ...(run.analysis?.proposed_action || {}), description: editedComment } };
      if (proposedType === "close_ticket" || proposedType === "escalate") {
        // Always sent, null when the box is empty: the edit is merged over the analysis, so a key that is left out
        // keeps the model's ORIGINAL draft and it is mailed although the technician deleted it.
        return { ...base, customer_reply_draft: editedReply.trim() ? editedReply : null };
      }
      return base;
    }
    return undefined;
  };

  // The form used to be seeded once, at mount: a page opened while the run was still analysing showed an EMPTY draft
  // once the analysis arrived, and saving it sent an empty customer message. Seed from the CURRENT analysis whenever
  // the form is opened (typing inside an open form is never overwritten by a poll).
  const seedEditFromRun = () => {
    setEditedReply(run.analysis?.customer_reply_draft || "");
    setEditedComment(run.analysis?.proposed_action?.description || "");
    setFeedbackEditType(_defaultEditType);
  };
  const blankCustomerMessage = proposedType === "send_customer_message" && !editedReply.trim();

  const feedbackMutation = useMutation({
    mutationFn: () =>
      agentRunsApi.feedback(run.id, {
        edit_type: feedbackEditType,
        reason:    feedbackReason || undefined,
        remember_for_customer: rememberForCustomer && feedbackReason.trim() !== "" ? true : undefined,
        remember_resolution: rememberResolution && feedbackReason.trim() !== "" ? true : undefined,
        original_ai_output: run.analysis ?? undefined,
        technician_edited_output: _buildEditedOutput(),
      }),
    onSuccess: () => {
      addNotification("משוב נשמר והריצה אושרה ✓", "success");
      setShowFeedback(false);
      setFeedbackReason("");
      setRememberForCustomer(false);
      setRememberResolution(false);
      if (onApproveSuccess) onApproveSuccess();
      else qc.invalidateQueries({ queryKey: ["agentRun", run.id] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בשמירת המשוב", "error"),
  });

  const isFailed = run.status === "failed";
  // Each control follows the permission the backend route enforces: approve/reject/edit/stop need agents_approve,
  // retry/resume need agents_run (routers/agent_runs.py). A resume also needs an analysis to resume into: without
  // one the backend answers 200 and leaves the run paused.
  const mayApprove = canAgent("agents_approve");
  const mayRun     = canAgent("agents_run");
  const showReview = REVIEWABLE.has(run.status) && mayApprove;
  const showResume = run.status === "paused" && mayRun && Boolean(run.analysis);
  const showRetry  = isFailed && mayRun;
  const showStop   = STOPPABLE.has(run.status) && mayApprove;
  if (!showReview && !showResume && !showRetry && !showStop) return null;

  return (
    <Card>
      <div className="flex flex-wrap gap-2">
        {showReview && (
          <>
            <button
              type="button"
              onClick={() => onAction("approve")}
              disabled={busy || showFeedback || showReject}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              title="קיצור מקשים: A"
            >
              אשר
            </button>
            <button
              type="button"
              onClick={() => { if (!showFeedback) seedEditFromRun(); setShowFeedback(!showFeedback); setShowReject(false); }}
              className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              ערוך ואשר
            </button>
            <button
              type="button"
              onClick={() => { setShowReject(!showReject); setShowFeedback(false); }}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              title="קיצור מקשים: R"
            >
              דחה
            </button>
          </>
        )}
        {showResume && (
          <button
            type="button"
            onClick={() => onAction("resume")}
            disabled={busy}
            className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            המשך
          </button>
        )}
        {showRetry && (
          <button
            type="button"
            onClick={() => onAction("retry")}
            disabled={busy}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            נסה שוב
          </button>
        )}
        {showStop && (
          <button
            type="button"
            onClick={() => onAction("stop")}
            disabled={busy}
            className="border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            עצור
          </button>
        )}
      </div>

      {/* Edit & Approve form */}
      {showFeedback && (
        <div className="space-y-3 border-t pt-3">
          <p className="text-xs text-gray-500">
            תיקון הניתוח יישמר ב-log המשוב ואז הריצה תאושר אוטומטית.
          </p>

          {/* Inline content editor for send_customer_message */}
          {proposedType === "send_customer_message" && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                ערוך את טיוטת התגובה ללקוח:
              </label>
              <textarea
                rows={5}
                dir="auto"
                value={editedReply}
                onChange={(e) => setEditedReply(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          )}

          {/* Inline content editor for add_comment / close_ticket / escalate */}
          {["add_comment", "close_ticket", "escalate"].includes(proposedType) && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {{
                  add_comment:  "ערוך את תוכן ההערה הפנימית:",
                  close_ticket: "ערוך הודעת סגירה (פנימית):",
                  escalate:     "ערוך סיבת הסלמה:",
                }[proposedType]}
              </label>
              <textarea
                rows={proposedType === "add_comment" ? 5 : 3}
                dir="auto"
                value={editedComment}
                onChange={(e) => setEditedComment(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          )}

          {/* Customer email editor for close_ticket / escalate — this draft is sent to the customer */}
          {(proposedType === "close_ticket" || proposedType === "escalate") && (
            <div>
              <label className="block text-xs font-medium text-blue-700 mb-1">
                {proposedType === "close_ticket"
                  ? "✉ מייל סגירה ללקוח — יישלח אוטומטית עם האישור:"
                  : "✉ מסר אישור הסלמה ללקוח — יישלח אוטומטית עם האישור:"}
              </label>
              <textarea
                rows={4}
                dir="auto"
                value={editedReply}
                onChange={(e) => setEditedReply(e.target.value)}
                className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-400"
              />
            </div>
          )}

          <div className="flex gap-2 items-center">
            <label className="text-xs text-gray-500 shrink-0">סוג תיקון:</label>
            <select
              value={feedbackEditType}
              onChange={(e) => setFeedbackEditType(e.target.value)}
              className="border rounded-lg px-2 py-1 text-xs bg-white"
            >
              {CORRECTION_EDIT_TYPES.map((v) => (
                <option key={v} value={v}>{EDIT_TYPE_LABEL[v]}</option>
              ))}
            </select>
          </div>
          <textarea
            rows={2}
            placeholder="הסבר מה היה שגוי בניתוח ה-AI (אופציונלי)"
            value={feedbackReason}
            onChange={(e) => setFeedbackReason(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          {canAgent("agents_manage") && (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={rememberForCustomer}
                disabled={feedbackReason.trim() === ""}
                onChange={(e) => setRememberForCustomer(e.target.checked)}
              />
              שמור את ההסבר כזיכרון מאומת של הלקוח
            </label>
          )}
          {canAgent("agents_manage") && proposedType === "close_ticket" && (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={rememberResolution}
                disabled={feedbackReason.trim() === ""}
                onChange={(e) => setRememberResolution(e.target.checked)}
              />
              ההסבר מאשר שהפתרון עבד — שמור כתוצאת פתרון מאומתת בזיכרון הלקוח
            </label>
          )}
          {feedbackMutation.isError && (
            <p className="text-xs text-red-600">
              {apiErrorMessage(feedbackMutation.error)}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => feedbackMutation.mutate()}
              disabled={feedbackMutation.isPending || blankCustomerMessage}
              title={blankCustomerMessage ? "לא ניתן לאשר הודעה ריקה ללקוח — דחו את הריצה אם אין מה לשלוח" : undefined}
              className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {feedbackMutation.isPending ? "שומר..." : "שמור ואשר"}
            </button>
            <button
              type="button"
              onClick={() => setShowFeedback(false)}
              className="border px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50"
            >
              ביטול
            </button>
          </div>
        </div>
      )}

      {/* Reject form */}
      {showReject && (
        <div className="space-y-2 border-t pt-3">
          <div className="flex flex-wrap gap-1.5">
            {[..._REJECT_QUICK_REASONS, ...historicalReasons.filter((r) => !_REJECT_QUICK_REASONS.includes(r))].map((r) => {
              const isHistorical = !_REJECT_QUICK_REASONS.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRejectReason(r)}
                  className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                    rejectReason === r
                      ? "bg-red-100 border-red-400 text-red-700 font-medium"
                      : isHistorical
                        ? "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
                        : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {r}
                </button>
              );
            })}
          </div>
          <textarea
            rows={2}
            placeholder="סיבת הדחייה (אופציונלי)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => { onAction("reject", rejectReason); setShowReject(false); }}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-lg text-sm font-medium"
          >
            אשר דחייה
          </button>
        </div>
      )}
    </Card>
  );
}

// ─── AgentRunDetailPage ───────────────────────────────────────────────────────

// Matches AgentRunPanel.jsx's own TERMINAL_STATUSES: rejected/failed runs are just as finished as
// approved/completed/resolved ones (the backend's AgentRun model sets finished_at for all five).
// The rate endpoint's own _RATEABLE_STATUSES (agent_runs.py) still refuses non-completed runs with
// a 409 shown via _onMutationError -- so widening this only surfaces that same graceful refusal
// here too, instead of hiding the control outright and diverging from the sibling component.
const _TERMINAL_STATUSES = new Set(["approved", "rejected", "completed", "resolved", "failed"]);

// Keyed by the run id: navigating from one run to another (the "pending reviews" button, approve/reject advancing to the next run,
// retry) reused this component, so the previous run's thumbs rating and an OPEN "edit and approve" form (with its edited
// customer text) stayed on the next run whenever it was already in the query cache. "שמור ואשר" then sent run A's text to
// run B's customer.
export default function AgentRunDetailPage() {
  const { id } = useParams();
  return <AgentRunDetailInner key={id} />;
}

function AgentRunDetailInner() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || "/agent-runs";
  const { can: canAgent } = useAgentPermissions();
  const { addNotification } = useNotification();

  const [qualityRating, setQualityRating] = useState(null);
  const [ratingBusy, setRatingBusy] = useState(false);

  // Also poll while awaiting review/approval or waiting on the customer: scheduler.py can auto-fail a
  // run stuck in any of these (with no technician action) after 48h/24h, and the page's stale-cached
  // run would otherwise keep its approve/reject controls live against an already-terminal run.
  const ACTIVE = new Set([
    "pending", "analyzing", "diagnosing", "connection_active", "executing",
    "awaiting_technician_review", "awaiting_approval",
    "waiting_for_customer_connection", "waiting_for_customer_answer", "paused",
  ]);

  const { data: run, isLoading, isError } = useQuery({
    queryKey: ["agentRun", id],
    queryFn: () => agentRunsApi.get(id),
    enabled: !!id,
    refetchInterval: (query) => ACTIVE.has(query.state.data?.status) ? 5000 : false,
  });

  const invalidateTicketCaches = useCallback((ticketId) => {
    if (!ticketId) return;
    qc.invalidateQueries({ queryKey: ["ticket", String(ticketId)] });
    qc.invalidateQueries({ queryKey: ["tickets"] });
  }, [qc]);

  // AgentRunPanel.jsx's own updateRun invalidates ["ticket", ticketId]/["tickets"] whenever a fresh
  // run object lands in a terminal status -- this page never did, for either its poll path (the
  // scheduler.py auto-fail this page's own ACTIVE/5000ms poll above exists to catch) or its
  // approve/reject/stop/resume mutations' invalidate(). TicketDetail/TicketsPage/Dashboard's AI-run
  // badges stayed stale until their own unrelated polls happened to catch up, whether a technician
  // watched a run auto-fail live on this dedicated page or acted on it directly here.
  const prevRunStatusRef = useRef(run?.status);
  useEffect(() => {
    const prevStatus = prevRunStatusRef.current;
    prevRunStatusRef.current = run?.status;
    if (run?.status && run.status !== prevStatus && _TERMINAL_STATUSES.has(run.status)) {
      invalidateTicketCaches(run.ticket_id);
    }
  }, [run?.status, run?.ticket_id, invalidateTicketCaches]);

  // No `= []` default: the trail must distinguish "the source reported no tokens" (an empty array)
  // from "the read has not answered yet" (undefined). Defaulting to [] would draw a clean "no tokens"
  // on every first render.
  const { data: approvalTokens, isLoading: approvalTokensLoading } = useQuery({
    queryKey: ["agentRunTokens", id],
    queryFn: () => agentRunsApi.approvalTokens(id),
    enabled: !!id && !!run,
    staleTime: 30000,
  });

  const { data: feedbackHistory = [] } = useQuery({
    queryKey: ["agentRunFeedback", id],
    queryFn: () => agentRunsApi.feedbackHistory(id),
    enabled: !!id && !!run,
    staleTime: 30000,
  });

  const { data: pendingRuns = [] } = useQuery({
    queryKey: ["agentRunsPending"],
    queryFn: () => agentRunsApi.listAll({ status: "awaiting_technician_review", limit: 20 }),
    staleTime: 30000,
    refetchOnWindowFocus: true,
    // Its own manual invalidate() only fires on THIS page's approve/reject/stop/resume -- a run in the
    // list getting approved/rejected/auto-failed anywhere else (another tab, AgentRunPanel, the
    // scheduler's 48h/24h auto-fail) never touches this cache otherwise, unlike agents-layout-badge/
    // agents-dash which self-heal via their own refetchInterval while the tab stays focused.
    refetchInterval: 30000,
  });
  const _PRIO_RANK = { high: 0, medium: 1, low: 2 };
  const otherPending = pendingRuns
    .filter((r) => r.id !== id)
    .sort((a, b) => {
      const pa = _PRIO_RANK[a.ticket_priority] ?? 3;
      const pb = _PRIO_RANK[b.ticket_priority] ?? 3;
      if (pa !== pb) return pa - pb;
      return new Date(a.updated_at) - new Date(b.updated_at);
    });

  const { data: feedbackStats } = useQuery({
    queryKey: ["feedbackStats"],
    queryFn: () => agentRunsApi.feedbackStats(),
    staleTime: 120000,
  });
  const historicalReasons = Object.keys(feedbackStats?.rejection_reason_distribution || {}).slice(0, 6);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["agentRun", id] });
    qc.invalidateQueries({ queryKey: ["agentRunTokens", id] });
    qc.invalidateQueries({ queryKey: ["agentRunFeedback", id] });
    qc.invalidateQueries({ queryKey: ["agentRunsPending"] });
    // The three independent pending-count badges (nav, Dashboard, AgentsPage) each poll agentsApi.list()
    // under their own query key with no shared prefix, so an approve/reject here won't reach them without
    // an explicit invalidation of each.
    qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
    qc.invalidateQueries({ queryKey: ["agents-dash"] });
    qc.invalidateQueries({ queryKey: ["agents"] });
  }, [qc, id]);

  const invalidateAndAdvance = useCallback(() => {
    invalidate();
    // Approve/reject navigate away immediately below, which unmounts this page before the
    // ["agentRun", id] invalidation above can refetch and trip the terminal-status effect further
    // up (the one invalidating ["ticket", ...]/["tickets"]) -- that effect only fires for stop/
    // resume, which don't navigate away. Approve/reject already know at this point that the run
    // just went terminal, so invalidate the ticket caches directly instead of relying on it.
    invalidateTicketCaches(run?.ticket_id);
    const next = otherPending?.[0];
    if (next?.id) navigate(`/agent-runs/${next.id}`, { state: { from: returnTo } });
    else navigate(returnTo);
  }, [otherPending, navigate, run?.ticket_id, invalidateTicketCaches, invalidate, returnTo]);

  const _onMutationError = (err, fallback) => {
    addNotification(err?.response?.data?.detail || fallback, "error");
  };

  const approveMutation  = useMutation({
    mutationFn: () => agentRunsApi.approve(id),
    onSuccess: () => { addNotification("הריצה אושרה ✓", "success"); invalidateAndAdvance(); },
    onError: (err) => _onMutationError(err, "שגיאה באישור הריצה"),
  });
  const rejectMutation   = useMutation({
    mutationFn: (r) => agentRunsApi.reject(id, r),
    onSuccess: () => { addNotification("הריצה נדחתה", "info"); invalidateAndAdvance(); },
    onError: (err) => _onMutationError(err, "שגיאה בדחיית הריצה"),
  });
  const stopMutation     = useMutation({
    mutationFn: () => agentRunsApi.stop(id, "operator"),
    onSuccess: () => { addNotification("הריצה נעצרה", "info"); invalidate(); },
    onError: (err) => _onMutationError(err, "שגיאה בעצירת הריצה"),
  });
  const resumeMutation   = useMutation({
    mutationFn: () => agentRunsApi.resume(id),
    onSuccess: () => { addNotification("הריצה הופעלה מחדש", "success"); invalidate(); },
    onError: (err) => _onMutationError(err, "שגיאה בהפעלה מחדש"),
  });
  const retryMutation    = useMutation({
    mutationFn: () => agentRunsApi.retry(id),
    onSuccess: (newRun) => {
      addNotification("ריצה חדשה נוצרה לניסיון חוזר", "success");
      qc.invalidateQueries({ queryKey: ["agentRun", id] });
      if (newRun?.id) navigate(`/agent-runs/${newRun.id}`, { state: { from: returnTo } });
    },
    onError: (err) => _onMutationError(err, "שגיאה בניסיון החוזר"),
  });
  const rerunMutation    = useMutation({
    mutationFn: () => agentRunsApi.start(run?.ticket_id, run?.agent_id),
    onSuccess: (newRun) => {
      addNotification("ניתוח AI חדש הופעל", "success");
      if (newRun?.id) navigate(`/agent-runs/${newRun.id}`, { state: { from: returnTo } });
    },
    // No onError toast — this mutation already has an inline error display
    // right below its trigger button (rerunMutation.isError block).
  });

  const handleAction = useCallback((type, payload) => {
    if (type === "approve") approveMutation.mutate();
    else if (type === "reject") rejectMutation.mutate(payload);
    else if (type === "stop") stopMutation.mutate();
    else if (type === "resume") resumeMutation.mutate();
    else if (type === "retry") retryMutation.mutate();
  }, [approveMutation, rejectMutation, stopMutation, resumeMutation, retryMutation]);

  // A decision already in flight must not be repeated: a second approve/reject sent before the
  // first returns (double click, two quick key presses) is a second attempt at a side-effecting action.
  const decisionPending = [approveMutation, rejectMutation, stopMutation, resumeMutation, retryMutation]
    .some((m) => m.isPending);

  // Set by ApprovalControls whenever its own edit/reject form is open: a bare "A"/"R" keypress
  // (e.g. focus left the textarea onto a button) must not silently approve/reject with the
  // ORIGINAL AI draft or an empty reason while the technician has unsaved input open in that form.
  const [reviewFormOpen, setReviewFormOpen] = useState(false);

  const isReviewable = run && REVIEWABLE.has(run.status) && canAgent("agents_approve");
  useEffect(() => {
    if (!isReviewable) return;
    const onKey = (e) => {
      const action = reviewShortcutAction(e);
      if (!action) return;
      e.preventDefault();
      if (decisionPending || reviewFormOpen) return;
      if (action === "approve") approveMutation.mutate();
      else handleAction("reject");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isReviewable, decisionPending, reviewFormOpen, approveMutation, handleAction]);

  useEffect(() => {
    if (!feedbackHistory.length || qualityRating) return;
    const existing = feedbackHistory.find((f) => f.edit_type === "quality_rating");
    if (existing) setQualityRating(existing.technician_edited_output?.rating ?? null);
  }, [feedbackHistory, qualityRating]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (isError || !run) {
    return (
      <div className="text-center py-16">
        <p className="text-red-600 text-sm">לא ניתן לטעון את הריצה</p>
        <Link to={returnTo} className="text-xs text-brand-600 hover:underline mt-2 block">← חזרה לריצות</Link>
      </div>
    );
  }

  const statusColor = STATUS_COLOR[run.status] || "bg-gray-100 text-gray-600 border-gray-200";
  const durationMs = run.duration_ms != null
    ? run.duration_ms
    : (run.started_at && run.finished_at
        ? Math.round(new Date(run.finished_at) - new Date(run.started_at))
        : null);
  const runDurationLabel = durationMs == null ? "—"
    : durationMs < 1000 ? `${durationMs}ms`
    : durationMs < 60000 ? `${(durationMs / 1000).toFixed(1)}ש`
    : `${Math.round(durationMs / 60000)}ד`;

  return (
    <div className="space-y-5 max-w-4xl mx-auto" dir="rtl">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <Link to={returnTo} className="hover:text-gray-600">ריצות AI</Link>
          <span>/</span>
          <span className="text-gray-600 font-mono">{String(id).slice(0, 8)}…</span>
        </div>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1">
            {run.ticket_number ? (
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/tickets/${run.ticket_id}?from=${encodeURIComponent(returnTo)}`}
                  className="text-xl font-bold text-brand-600 hover:underline"
                >
                  קריאה #{run.ticket_number}
                </Link>
                {run.ticket_priority === "high" && (
                  <span className="text-xs text-red-600 font-semibold bg-red-50 border border-red-200 rounded-full px-2 py-0.5" title="עדיפות גבוהה">⚠ גבוהה</span>
                )}
                {run.ticket_status === "closed" && (
                  <span className="text-xs text-gray-500 bg-gray-100 border border-gray-300 rounded-full px-2 py-0.5" title="קריאה סגורה">✓ סגורה</span>
                )}
                {run.ticket_status === "waiting" && (
                  <span className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5" title="ממתין לתגובת לקוח">⏳ ממתין</span>
                )}
                {run.ticket_status === "pending" && (
                  <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" title="ממתין לאישור">⌛ בהמתנה</span>
                )}
              </div>
            ) : (
              <h1 className="text-xl font-bold text-gray-800">ריצת AI</h1>
            )}
            {run.ticket_title && <p className="text-sm text-gray-500">{run.ticket_title}</p>}
            {run.ticket_company_name && (
              <p className="text-xs text-gray-400">{run.ticket_company_name}</p>
            )}
            {run.agent_name && (
              <p className="text-xs text-gray-400">
                סוכן:{" "}
                <Link to={`/agents/${run.agent_id}`} className="text-brand-600 hover:underline">
                  {run.agent_name}
                </Link>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span data-testid="run-status-badge" className={`px-3 py-1 rounded-full border text-sm font-medium ${statusColor}`}>
              {runStatusLabel(run.status)}
            </span>
            {["completed", "resolved", "approved"].includes(run.status) && run.ticket_id && run.agent_id && (
              <button
                type="button"
                onClick={() => rerunMutation.mutate()}
                disabled={rerunMutation.isPending}
                className="text-xs border border-gray-300 text-gray-500 rounded-full px-3 py-1 hover:bg-gray-50 transition-colors disabled:opacity-50"
                title="הפעל ניתוח AI חדש לקריאה זו"
              >
                {rerunMutation.isPending ? "מתחיל..." : "ניתח מחדש"}
              </button>
            )}
            {otherPending.length > 0 && (
              <button
                onClick={() => navigate(`/agent-runs/${otherPending[0].id}`, { state: { from: returnTo } })}
                className="flex items-center gap-1 text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-full px-3 py-1 hover:bg-amber-100 transition-colors"
                title={`עבור לסקירה הממתינה הבאה — ${otherPending.length} ממתינות`}
              >
                סקירות ממתינות
                <span className="bg-amber-200 rounded-full px-1.5 py-0.5 font-semibold text-amber-800">
                  {otherPending.length}
                </span>
                ←
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Meta row */}
      <div className="bg-white rounded-xl border shadow-sm px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-xs">
        <div>
          <p className="text-gray-500 mb-0.5">התחיל</p>
          <p className="text-gray-800">{fmtDate(run.started_at || run.created_at)}</p>
        </div>
        <div>
          {REVIEWABLE.has(run.status) ? (
            <>
              <p className="text-gray-500 mb-0.5">ממתין מאז</p>
              <p className={`font-medium ${
                (() => {
                  const mins = run.updated_at ? Math.floor((Date.now() - new Date(run.updated_at).getTime()) / 60000) : 0;
                  return mins >= 60 ? "text-red-600" : mins >= 30 ? "text-amber-600" : "text-gray-800";
                })()
              }`}>
                {(() => {
                  const mins = run.updated_at ? Math.floor((Date.now() - new Date(run.updated_at).getTime()) / 60000) : 0;
                  return mins < 60 ? `${mins} דק'` : `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")} ש'`;
                })()}
              </p>
            </>
          ) : (
            <>
              <p className="text-gray-500 mb-0.5">הסתיים</p>
              <p className="text-gray-800">{fmtDate(run.finished_at)}</p>
            </>
          )}
        </div>
        <div>
          <p className="text-gray-500 mb-0.5">משך</p>
          <p className="text-gray-800">{runDurationLabel}</p>
        </div>
        <div>
          <p className="text-gray-500 mb-0.5">פעולות</p>
          <p className="text-gray-800">{run.actions?.length || 0}</p>
        </div>
      </div>

      {/* Decision brief — what the operator is approving, before the controls below */}
      {REVIEWABLE.has(run.status) && <ApprovalDecisionBrief run={run} />}

      {/* Controls — only shown to users with approve permission */}
      {(canAgent("agents_approve") || canAgent("agents_run")) && <ApprovalControls run={run} onAction={handleAction} historicalReasons={historicalReasons} onApproveSuccess={invalidateAndAdvance} busy={decisionPending} onFormOpenChange={setReviewFormOpen} />}

      {/* Failure & recovery brief — what failed, what was preserved, what is needed next. The
          approve/reject/retry controls below stay the only place a decision is made. */}
      {_RECOVERY_BRIEF_STATUSES.has(run.status) && (
        <FailureRecoveryPanel run={run} canRun={canAgent("agents_run")} />
      )}

      {/* MASTER_FLOW §15A's run timeline: analysis -> proposal -> approval -> execution ->
          verification, then latency/cost. Read-only; every decision stays in the controls above.
          The detailed cards below (analysis, QA inspector, feedback) are the drill-down. */}
      <RunOperationsTrail run={run} approvalTokens={approvalTokens} tokensLoading={approvalTokensLoading} />

      {/* Per-run Skill routing evidence — the record the wiring seam stored on the run, read
          verbatim. Read-only: no control and no permission of its own. */}
      <SkillRoutingEvidencePanel raw={run.context?.skill_routing} />

      {/* Quality rating for terminal runs */}
      {/* POST /rate needs agents_run, so the thumbs follow it (a viewer's click used to fail silently) */}
      {_TERMINAL_STATUSES.has(run.status) && canAgent("agents_run") && (
        <div className="bg-white rounded-xl border shadow-sm px-5 py-3 flex items-center gap-3">
          <span className="text-sm text-gray-500">האם הניתוח היה מועיל?</span>
          {qualityRating ? (
            <span className="text-sm text-gray-600">
              {qualityRating === "thumbs_up" ? "👍 תודה!" : "👎 תודה על המשוב"}
            </span>
          ) : (
            <>
              <button
                type="button"
                disabled={ratingBusy}
                onClick={async () => {
                  setRatingBusy(true);
                  try { await agentRunsApi.rate(run.id, "thumbs_up"); setQualityRating("thumbs_up"); }
                  catch (err) { _onMutationError(err, "שגיאה בשמירת הדירוג"); } finally { setRatingBusy(false); }
                }}
                className="text-2xl leading-none hover:scale-110 transition-transform disabled:opacity-50"
                title="מועיל"
              >👍</button>
              <button
                type="button"
                disabled={ratingBusy}
                onClick={async () => {
                  setRatingBusy(true);
                  try { await agentRunsApi.rate(run.id, "thumbs_down"); setQualityRating("thumbs_down"); }
                  catch (err) { _onMutationError(err, "שגיאה בשמירת הדירוג"); } finally { setRatingBusy(false); }
                }}
                className="text-2xl leading-none hover:scale-110 transition-transform disabled:opacity-50"
                title="לא מועיל"
              >👎</button>
            </>
          )}
        </div>
      )}

      {/* Result summary */}
      {run.result_summary && (
        <Card title="תוצאה">
          <p className="text-sm text-gray-700">{run.result_summary}</p>
          {["approved", "completed", "resolved"].includes(run.status) && run.context?.technician && (
            <p className="mt-2 text-xs text-green-700 bg-green-50 border border-green-100 rounded px-2 py-1">
              אושר על ידי {run.context.technician}
            </p>
          )}
          {run.status === "rejected" && (
            <div className="mt-2 space-y-2">
              {run.context?.reject_reason && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1">
                  סיבת דחייה: {run.context.reject_reason}
                  {run.context.technician && (
                    <span className="mr-1 text-red-400"> · נדחה על ידי {run.context.technician}</span>
                  )}
                </p>
              )}
              {/* POST /agent-runs/ needs agents_run */}
              {canAgent("agents_run") && (
                <button
                  type="button"
                  onClick={() => rerunMutation.mutate()}
                  disabled={rerunMutation.isPending}
                  className="text-xs border border-brand-300 text-brand-600 rounded-lg px-3 py-1.5 hover:bg-brand-50 transition-colors disabled:opacity-50"
                >
                  {rerunMutation.isPending ? "מתחיל ניתוח..." : "בקש ניתוח AI חדש"}
                </button>
              )}
              {rerunMutation.isError && (
                <p className="text-xs text-red-600">{detailText(rerunMutation.error?.response?.data?.detail, "שגיאה בהפעלת ניתוח")}</p>
              )}
            </div>
          )}
          {run.context?.kb_articles_used?.length > 0 && (
            <div className="mt-2 rounded-md border border-blue-100 bg-blue-50/60 px-2.5 py-2">
              <p className="text-xs font-semibold text-blue-600 mb-1">ידע שנוצל בניתוח</p>
              <ul className="space-y-0.5">
                {run.context.kb_articles_used.map((a) => (
                  <li key={a.id} className="text-xs text-blue-700">
                    <Link to={`/knowledge?search=${encodeURIComponent(a.title)}`} className="hover:underline">• {a.title}</Link>
                    {(a.scope || a.updated_at) && (
                      <span className="mr-2 text-[10px] text-blue-500">
                        {a.scope === "customer" ? "לקוח" : "ארגון"}
                        {a.updated_at ? ` · עודכן ${new Date(a.updated_at).toLocaleDateString("he-IL")}` : ""}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {run.context?.similar_ticket_references?.length > 0 && (
            <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2">
              <p className="text-xs font-semibold text-gray-500 mb-1">קריאות דומות ששימשו כהקשר</p>
              <ul className="space-y-1">
                {run.context.similar_ticket_references.map((ref, i) => {
                  const title = typeof ref === "string" ? ref : ref.title;
                  const resolution = typeof ref === "string" ? null : ref.resolution_note;
                  const ticketId = typeof ref === "object" ? ref.id : null;
                  const ticketNum = typeof ref === "object" ? ref.ticket_number : null;
                  return (
                    <li key={i} className="text-xs text-gray-600">
                      {ticketId ? (
                        <Link to={`/tickets/${ticketId}`} className="hover:underline text-brand-600">
                          • {ticketNum ? `#${ticketNum} — ` : ""}{title}
                        </Link>
                      ) : (
                        <span>• {title}</span>
                      )}
                      {resolution && (
                        <span className="block mr-3 mt-0.5 text-green-700 bg-green-50 border border-green-100 rounded px-1.5 py-0.5">
                          פתרון: {resolution}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {run.context?.execution_result && (() => {
            const er = run.context.execution_result;
            const isOk          = er.success && er.executed;
            const isNoop        = er.success && !er.executed;
            const isCrisisBlocked = !er.success && !er.executed && ((er.message || "").includes("מצב חירום") || (er.message || "").includes("Crisis Mode"));
            const isFailed      = !er.success && !isCrisisBlocked;
            return (
              <div className={`mt-2 rounded-md border px-2.5 py-2 text-xs ${
                isOk || isNoop    ? "border-green-200 bg-green-50 text-green-700"
                : isCrisisBlocked ? "border-red-200 bg-red-50 text-red-700"
                : isFailed        ? "border-amber-200 bg-amber-50 text-amber-700"
                :                   "border-gray-200 bg-gray-50 text-gray-500"
              }`}>
                <span className="font-semibold">
                  {isOk ? "✓ בוצע: " : isNoop ? "✓ " : isCrisisBlocked ? "⚠️ חסום (מצב חירום): " : "✗ שגיאה: "}
                </span>
                {er.message}
                {er.action_type && <span className="ml-1 text-gray-400">({actionTypeLabel(er.action_type)})</span>}
              </div>
            );
          })()}
        </Card>
      )}

      {/* Context signals — which AI context sections were injected */}
      {run.context?.context_signals && (() => {
        const cs = run.context.context_signals;
        const chips = [];
        if (cs.contact_history) {
          const recurring = cs.contact_history.recurring_issue;
          chips.push({ label: recurring ? "היסטוריה: בעיה חוזרת ⚠" : "היסטוריית לקוח", color: recurring ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-gray-100 text-gray-600 border-gray-200" });
        }
        if (cs.cluster_signal) {
          chips.push({ label: `${cs.cluster_signal.ticket_count} קריאות פתוחות דומות ⚠`, color: "bg-amber-50 text-amber-700 border-amber-200" });
        }
        if (cs.company_issue_patterns) chips.push({ label: "דפוסי בעיות חברה", color: "bg-gray-100 text-gray-600 border-gray-200" });
        if (cs.company_action_patterns) chips.push({ label: "היסטוריית פעולות חברה", color: "bg-gray-100 text-gray-600 border-gray-200" });
        if (cs.knowledge_gaps) chips.push({ label: `${cs.knowledge_gaps.count ?? ""} פערי ידע`, color: "bg-gray-100 text-gray-600 border-gray-200" });
        if (cs.rejection_context) chips.push({ label: "דחייה קודמת", color: "bg-red-50 text-red-600 border-red-200" });
        if (cs.prior_run) chips.push({ label: "ניתוח קודם", color: "bg-gray-100 text-gray-600 border-gray-200" });
        if (cs.feedback_corrections) chips.push({ label: "תיקוני משוב", color: "bg-purple-50 text-purple-600 border-purple-200" });
        if (!chips.length && !cs.cluster_signal?.ticket_numbers?.length) return null;
        return (
          <div className="bg-white rounded-xl border shadow-sm px-5 py-3 space-y-2">
            <p className="text-xs font-medium text-gray-500">אותות הקשר שהוזנו לניתוח:</p>
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {chips.map((c, i) => (
                  <span key={i} className={`text-xs px-2 py-0.5 rounded border font-medium ${c.color}`}>{c.label}</span>
                ))}
              </div>
            )}
            {cs.cluster_signal?.ticket_numbers?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-xs font-medium text-amber-700 mb-1.5">קריאות פתוחות קשורות — בדוק תיאום:</p>
                <div className="flex flex-wrap gap-1.5">
                  {cs.cluster_signal.ticket_numbers.map((num) => (
                    <a key={num} href={`/tickets?search=%23${num}&status=all`} className="text-xs text-amber-900 bg-white border border-amber-300 px-2 py-0.5 rounded hover:bg-amber-100 transition-colors font-medium">
                      #{num}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Analysis */}
      <AnalysisSection analysis={run.analysis} resultSummary={run.result_summary && !run.analysis ? run.result_summary : null} />

      {/* Recent ticket activity — shown when reviewing reply proposals */}
      {run.context?.recent_activity && (
        <Card title="פעילות אחרונה בקריאה">
          <ActivityFeed raw={run.context.recent_activity} />
        </Card>
      )}

      {/* Ticket description — shown for new tickets (no prior activity) */}
      {!run.context?.recent_activity && run.context?.ticket_description && (
        <Card title="תיאור הקריאה המקורי">
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {run.context.ticket_description}
          </p>
        </Card>
      )}

      {/* Customer environment context */}
      {run.context?.customer_environment && (
        <Card title="סביבת לקוח — הקשר לניתוח">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs">
            {run.context.customer_environment.ms365_domain && (
              <KV label="M365 Domain" value={run.context.customer_environment.ms365_domain} mono />
            )}
            {run.context.customer_environment.endpoint_count != null && (
              <KV label="תחנות" value={run.context.customer_environment.endpoint_count} />
            )}
            {run.context.customer_environment.os_distribution && (
              <KV label="OS" value={Object.entries(run.context.customer_environment.os_distribution).map(([k,v]) => `${k}: ${v}`).join(", ")} />
            )}
            {run.context.customer_environment.backup_vendor && (
              <KV label="גיבוי" value={run.context.customer_environment.backup_vendor} />
            )}
            {run.context.customer_environment.av_vendor && (
              <KV label="אנטי-וירוס" value={run.context.customer_environment.av_vendor} />
            )}
            {run.context.customer_environment.vpn_vendor && (
              <KV label="VPN" value={run.context.customer_environment.vpn_vendor} />
            )}
            {run.context.customer_environment.asset_count != null && (
              <KV label="נכסים" value={run.context.customer_environment.asset_count} />
            )}
            {run.context.customer_environment.infrastructure_notes && (
              <div className="col-span-full">
                <KV label="הערות תשתית" value={run.context.customer_environment.infrastructure_notes} />
              </div>
            )}
          </div>
        </Card>
      )}

      {/* PROD-QA-003 / MASTER_FLOW §5A.4: the run's decision inputs, findings and closure
          eligibility, read-only from GET /{run_id}/qa-inspection. The operations trail above
          summarizes the same projection's verdict; this panel is the full drill-down it points to. */}
      <RunQaInspectorPanel runId={run.id} />

      {/* Corrections and learning history. */}
      {feedbackHistory.length > 0 && (
        <Card title="היסטוריית תיקונים ולמידה">
          <div className="space-y-2">
            {feedbackHistory.map((feedback) => (
              <FeedbackHistoryRow key={feedback.id} feedback={feedback} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
